import { Icon, Spinner, ThemeColor } from "@deligoo/ui"
import clsx from "clsx"
import { CSSProperties, forwardRef, HTMLAttributes } from "react"

import cls from "./Table.module.scss"

type TableProps = HTMLAttributes<HTMLDivElement> & {
  columns?: string
  minWidth?: string
  loading?: boolean
  error?: boolean
  empty?: boolean
  virtualized?: boolean
}

export const Table = ({
  columns,
  minWidth,
  loading,
  error,
  empty,
  virtualized,
  style,
  children,
  className,
  ...props
}: TableProps) => {
  const columnsStyles = columns ? ({ "--columns": columns } as CSSProperties) : {}
  const minWidthStyles = minWidth ? ({ "--minWidth": minWidth } as CSSProperties) : {}

  if (loading) {
    return (
      <div className={clsx(cls.status)}>
        <Spinner className={cls.spinner} />
      </div>
    )
  }

  if (error) {
    return (
      <div className={clsx(cls.status)}>
        <Icon name="database" className={cls.icon} />
        Wystąpił błąd podczas pobierania danych
      </div>
    )
  }

  return (
    <div className={clsx(cls.wrapper, virtualized && cls.virtualized)}>
      <div
        className={clsx(cls.table, empty && cls.empty, className)}
        style={{ ...minWidthStyles, ...columnsStyles, ...style }}
        {...props}
      >
        {children}

        {empty && (
          <div className={clsx(cls.status, cls.error)}>
            <Icon name="box" className={cls.icon} />
            Brak danych do wyświetlenia
          </div>
        )}
      </div>
    </div>
  )
}

const Card = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={clsx(cls.card, className)} {...props}>
      {children}
    </div>
  )
}

const Thead = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={clsx(cls.thead, className)} {...props}>
      {children}
    </div>
  )
}

const Tbody = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={clsx(cls.tbody, className)} {...props}>
      {children}
    </div>
  )
}

export type ThProps = {
  sortedBy?: "asc" | "desc"
} & HTMLAttributes<HTMLDivElement>

const Th = ({ sortedBy, children, className, onClick, ...props }: ThProps) => {
  return (
    <div className={clsx(cls.th, onClick && cls.thAction, className)} onClick={onClick} {...props}>
      <span className={cls.thLabel}>
        {children}

        {sortedBy && (
          <Icon
            name={sortedBy === "asc" ? "short-arrow-up" : "short-arrow-down"}
            className={cls.sortByIcon}
            size="tiny"
          />
        )}
      </span>
    </div>
  )
}

type TrProps = HTMLAttributes<HTMLDivElement> & {
  indicatorColor?: ThemeColor
  loading?: boolean
}

const Tr = forwardRef(({ indicatorColor, loading, children, className, onClick, ...props }: TrProps, ref) => {
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>} // FUCK THIS SHIT
      className={clsx(cls.tr, indicatorColor && [cls.indicator, cls[indicatorColor]], onClick && cls.action, className)}
      onClick={onClick}
      {...props}
    >
      {children}

      {loading && (
        <div className={clsx(loading && cls.loading)}>
          <Spinner className={cls.spinner} size="large" />
        </div>
      )}
    </div>
  )
})

type TdProps = HTMLAttributes<HTMLDivElement> & {
  indicatorLabel?: boolean
}

const Td = ({ indicatorLabel, children, className, ...props }: TdProps) => {
  return (
    <div className={clsx(cls.td, indicatorLabel && cls.indicatorLabel, className)} {...props}>
      {children}
    </div>
  )
}

Table.Card = Card
Table.Thead = Thead
Table.Tbody = Tbody
Table.Th = Th
Table.Tr = Tr
Table.Td = Td
